export const META_PIXEL_ID = '1050955704359293'

type FbqFn = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue?: unknown[]
  push?: unknown
  loaded?: boolean
  version?: string
}

declare global {
  interface Window {
    fbq?: FbqFn
    _fbq?: FbqFn
  }
}

const firedEvents = new Set<string>()
let initialized = false

/**
 * Injects the official Meta Pixel snippet into <head> asynchronously.
 * Safe to call multiple times — it only ever installs once.
 */
export const initMetaPixel = (): void => {
  if (typeof window === 'undefined' || initialized) return
  initialized = true

  if (!window.fbq) {
    const fbq: FbqFn = function (...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, args)
      } else {
        fbq.queue?.push(args)
      }
    } as FbqFn

    fbq.queue = []
    fbq.loaded = true
    fbq.version = '2.0'
    fbq.push = fbq

    window.fbq = fbq
    window._fbq = fbq

    const script = document.createElement('script')
    script.async = true
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'
    document.head.appendChild(script)

    const noscript = document.createElement('noscript')
    const img = document.createElement('img')
    img.height = 1
    img.width = 1
    img.style.display = 'none'
    img.alt = ''
    img.src = `https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`
    noscript.appendChild(img)
    document.head.appendChild(noscript)
  }

  window.fbq?.('init', META_PIXEL_ID)
  window.fbq?.('track', 'PageView')
}

/** Fires a standard Meta Pixel event. */
export const trackEvent = (
  event: string,
  params?: Record<string, unknown>,
): void => {
  if (typeof window === 'undefined') return
  try {
    window.fbq?.('track', event, params)
  } catch {
    /* never break the experience because of analytics */
  }
}

/**
 * Fire this from the onClick of any button/link that opens the Ringana webshop.
 * (No webshop link exists in the experience yet — attach it when one is added.)
 */
export const trackWebshopClick = (destination?: string): void => {
  trackEventOnce(
    'InitiateCheckout',
    { content_name: 'Ringana webshop', destination },
    destination,
  )
}

/** Fires a standard event at most once per session, keyed by event + optional id. */
export const trackEventOnce = (
  event: string,
  params?: Record<string, unknown>,
  key?: string,
): void => {
  const eventKey = key ? `${event}:${key}` : event
  if (firedEvents.has(eventKey)) return
  firedEvents.add(eventKey)
  trackEvent(event, params)
}
